import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { useCloseBom } from "../../../api/boms";
import useBom from "../../../hooks/useBom";
import useEncodedBomId from "../../../hooks/useEncodedBomId";
import useNavigateToImportSummary from "../../../hooks/useNavigateToImportSummary";
import useBomWizardStore from "../../../stores/bomWizardStore";
import useFileImportStore from "../../../stores/fileImportStore";

function useHandleClosedBom(bom) {
  const currentImportIndex = useFileImportStore((s) => s.currentIndex);
  const [closedBomHandlerAction, setClosedBomHandlerAction] = useState(null);
  const navigateToImportSummary = useNavigateToImportSummary();
  const unparsedBoms = useFileImportStore((s) => s.unparsedBOMs);
  const navigate = useNavigate();
  const { encodedId } = useEncodedBomId(bom?.BOMID);

  useEffect(() => {
    const hasUnparsed = unparsedBoms && Array.isArray(unparsedBoms) && unparsedBoms.length > 0;
    const importsRemaining = unparsedBoms.length - (currentImportIndex + 1);
    if (bom && bom.BOM_State === "CLOSED") {
      if (hasUnparsed && importsRemaining > 0) {
        setClosedBomHandlerAction("continue");
      } else if (unparsedBoms && unparsedBoms.length > 1 && importsRemaining === 0) {
        setClosedBomHandlerAction("summary");
      } else {
        setClosedBomHandlerAction("detail");
      }
    }

    return () => {
      setClosedBomHandlerAction(null);
    };
  }, [bom, unparsedBoms, currentImportIndex]);

  useEffect(() => {
    if (bom && bom.BOM_State === "CLOSED" && encodedId) {
      useFileImportStore.getState().clearState(true);
      navigate(`/dashboard/bom/${encodedId}/`);
    }
  }, [bom, encodedId, navigate]);

  useEffect(() => {
    if (bom) {
      if (closedBomHandlerAction === "summary") {
        navigateToImportSummary();
      }
    }
  }, [closedBomHandlerAction, navigateToImportSummary, bom]);
}

function useSubmission() {
  const bomId = useBomWizardStore((s) => s.bomId);
  const { bom } = useBom(bomId);
  const successfulImports = useFileImportStore((s) => s.successfulImports);
  const navigate = useNavigate();
  const navigateToImportSummary = useNavigateToImportSummary();
  useHandleClosedBom(bom);
  const { mutate: closeBom, isError } = useCloseBom();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(() => {
    setSubmitting(true);
    closeBom(bomId);
  }, [bomId, closeBom]);

  const handleCancel = useCallback(() => {
    if (successfulImports && successfulImports.length > 0) {
      navigateToImportSummary();
    } else {
      useFileImportStore.getState().clearState(true);
      navigate("/dashboard", { replace: true });
    }
  }, [navigateToImportSummary, navigate, successfulImports]);

  useEffect(() => {
    if (isError) {
      Swal.fire({
        title: "Error!",
        text: "Failed to submit your BOM. Please try again.",
        icon: "error",
      }).then(() => {
        setSubmitting(false);
      });
    }
  }, [isError]);

  return useMemo(() => ({ handleSubmit, submitting, handleCancel }), [handleSubmit, submitting, handleCancel]);
}

export default useSubmission;
